import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchClient } from "../lib/openapi-query";
import { queryKeys } from "../lib/query-client";
import { AuthService } from "../services/auth";
import type { User } from "../types/auth";

// Query for current user profile using fetchClient directly
export function useProfile() {
  // We need to check if token exists before making the query
  const token = AuthService.getStoredToken();

  // Using fetchClient directly with useQuery for better reliability
  return useQuery({
    queryKey: queryKeys.profile(),
    queryFn: async () => {
      const { data, error } = await fetchClient.GET("/profile");
      if (error) {
        throw error;
      }
      return data as User;
    },
    staleTime: 5 * 60_000, // 5 minutes
    retry: false, // Don't retry profile checks to avoid infinite loops
    enabled: !!token, // Only run if we have a token
    refetchOnMount: true, // Always refetch on mount to ensure fresh data
  });
}

// Mutation for login using fetchClient directly
export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      const { data, error } = await fetchClient.POST("/auth/login", {
        body: credentials,
      });
      if (error) {
        throw error;
      }
      return data;
    },
    onSuccess: async (data) => {
      // Store tokens and user data
      if (data) {
        AuthService.setTokens(data.accessToken, data.refreshToken);

        // Invalidate and refetch profile to ensure consistency
        await queryClient.invalidateQueries({ queryKey: queryKeys.profile() });
      }
    },
    onError: (error) => {
      console.error("Login failed:", error);
      // Clear any cached auth data
      void queryClient.removeQueries({ queryKey: queryKeys.auth });
    },
  });
}

// Mutation for registration using fetchClient directly
export function useRegister() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData: {
      email: string;
      password: string;
      firstName?: string;
      lastName?: string;
    }) => {
      const { data, error } = await fetchClient.POST("/auth/register", {
        body: userData,
      });
      if (error) {
        throw error;
      }
      return data;
    },
    onSuccess: (data) => {
      if (data) {
        // Store tokens and user data
        // AuthService.setTokens(data.accessToken, data.refreshToken);
        AuthService.setStoredUser(data);

        // Update the profile cache with the registered user
        void queryClient.setQueryData(queryKeys.profile(), data);

        // Invalidate and refetch profile to ensure consistency
        void queryClient.invalidateQueries({ queryKey: queryKeys.profile() });
      }
    },
    onError: (error) => {
      console.error("Registration failed:", error);
      // Clear any cached auth data
      void queryClient.removeQueries({ queryKey: queryKeys.auth });
    },
  });
}

// Mutation for logout using fetchClient directly
export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await fetchClient.POST("/auth/logout");
      if (error) {
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      // Clear auth tokens and user data
      AuthService.clearAuth();
      // Clear all cached data on logout
      queryClient.clear();
    },
    onError: (error) => {
      console.error("Logout failed:", error);
      // Clear auth and cache anyway since logout should always succeed locally
      AuthService.clearAuth();
      queryClient.clear();
    },
  });
}

// Hook to check if user is authenticated
export function useIsAuthenticated() {
  const { data, isLoading } = useProfile();
  return {
    isAuthenticated: !!data,
    user: data,
    isLoading,
  };
}

// Hook to get stored token without triggering a network request
export function useStoredAuth() {
  return {
    token: AuthService.getStoredToken(),
    user: AuthService.getStoredUser(),
    refreshToken: AuthService.getStoredRefreshToken(),
  };
}
